import { Fab, LinearProgress, List, ListItem, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import Box from "@mui/material/Box";
import { ProjectState } from "../../api/project";
import {
  useProjectProgress,
  useStartProcessingProject,
  useStopProcessingProject,
} from "../../api/use-project";
import { ToastContext } from "../../contexts/toast-context";
import { useContext, useEffect, useState } from "react";
import { UserContext } from "../../contexts/user-context";

export const ProjectProgress = ({ project, reloadProject }) => {
  const { t } = useTranslation(["project", "common"]);
  const { showError } = useContext(ToastContext);
  const { user } = useContext(UserContext);

  const isOwnProject = user && project?.createdById === user.id;
  const isAdmin = user && user.role === "admin";

  const processingSteps = [
    { name: "pending", percent: 0 },
    { name: "addPhotos", percent: 3 },
    { name: "alignPhotos", percent: 5 },
    { name: "optimizeCameras", percent: 10 },
    { name: "buildDepthMap", percent: 20 },
    { name: "buildPointCloud", percent: 30 },
    { name: "exportPointCloud", percent: 40 },
    { name: "convertPointCloud", percent: 50 },
    { name: "buildDem", percent: 70 },
    { name: "buildOrthomosaic", percent: 80 },
    { name: "exportCameras", percent: 97 },
  ];

  const [{ loading: startProcessingLoading }, executeStartProcessing] =
    useStartProcessingProject(project.id);
  const [{ loading: stopProcessingLoading }, executeStopProcessing] =
    useStopProcessingProject(project.id);
  const [{ data: progress }, executeProgress] = useProjectProgress(project.id);
  const [reloadProjectIfFailedOr100, setReloadProjectIfFailedOr100] =
    useState(false);

  const handleStart = async () => {
    try {
      await executeStartProcessing();
      reloadProject();
    } catch (e) {
      showError(t("genericError", { action: t("start") }));
    }
  };

  const handleStop = async () => {
    try {
      await executeStopProcessing();
      reloadProject();
    } catch (e) {
      showError(t("genericError", { action: t("stop") }));
    }
  };

  useEffect(() => {
    if (!progress) return;
    if (progress.percent === 100) {
      if (reloadProjectIfFailedOr100) {
        reloadProject();
      }
    } else if (progress.projectState === ProjectState.failed) {
      if (reloadProjectIfFailedOr100) {
        reloadProject();
      }
    }
  }, [progress, reloadProjectIfFailedOr100, reloadProject]);

  useEffect(() => {
    if (
      project.state === ProjectState.processing ||
      project.state === ProjectState.failed
    ) {
      setReloadProjectIfFailedOr100(false);
      executeProgress();
    }
  }, [project, executeProgress]);

  useEffect(() => {
    let id = null;
    if (project.state === ProjectState.processing)
      id = setInterval(() => {
        setReloadProjectIfFailedOr100(true);
        executeProgress();
      }, 1000);
    return () => {
      if (id) clearInterval(id);
    };
  }, [project, executeProgress]);

  return (
    <>
      <Typography variant="h6">
        {t("projectState", { state: t(`states.${project.state}`) })}
      </Typography>
      {project.state === ProjectState.processing && progress != null && (
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <LinearProgress
            variant="determinate"
            value={progress?.percent}
            sx={{ flex: 1 }}
          />
          <Box sx={{ ml: 2 }} />
          {progress?.percent}%
        </Box>
      )}
      <List sx={{ py: 3 }}>
        {processingSteps.map((pi) => {
          let color = "#ffffff";
          if (project.state === ProjectState.failed) {
            if (progress != null && progress <= pi.percent) color = "#ff0000";
          } else if (
            project.state === ProjectState.idle ||
            (project.state === ProjectState.processing &&
              progress != null &&
              progress?.percent < pi.percent)
          )
            color = "#5d5d5d";
          return (
            <ListItem
              key={pi.name}
              sx={{
                fontWeight: 700,
                color,
              }}
            >
              {t(`processingSteps.${pi.name}`)}
            </ListItem>
          );
        })}
      </List>
      {(isOwnProject || isAdmin) && (
        <>
          <Fab
            color="greenProject"
            sx={{ fontWeight: 700, p: 4, zIndex: 0 }}
            size="large"
            onClick={handleStart}
            disabled={
              project.state === ProjectState.processing ||
              project.media.length === 0 ||
              startProcessingLoading
            }
          >
            {t("start")}
          </Fab>
          <Fab
            color="redProject"
            sx={{ fontWeight: 700, ml: 3, p: 4, zIndex: 0 }}
            size="large"
            onClick={handleStop}
            disabled={
              project.state !== ProjectState.processing || stopProcessingLoading
            }
          >
            {t("stop")}
          </Fab>
        </>
      )}{" "}
    </>
  );
};
